import * as THREE from 'three';

interface LampPart {
  mat: THREE.MeshStandardMaterial;
  base: number;
}

interface LampSlot {
  root: THREE.Object3D;
  parts: LampPart[];
  glow: THREE.MeshBasicMaterial | null;
  glowBase: number;
  phase: number;
  speed: number;
  sick: boolean;
  blackout: number;
}

/**
 * Visual-only tube/lip pulse. Baked wall lighting stays put; no extra lights.
 */
export class LampFlicker {
  private readonly lamps: LampSlot[] = [];
  private t = 0;

  constructor(root: THREE.Object3D) {
    root.traverse((o) => {
      if (o.userData.destructible !== 'lamp' || o.userData.alarmBeacon) return;
      const parts: LampPart[] = [];
      const take = (mesh: THREE.Mesh | undefined) => {
        if (!mesh?.isMesh) return;
        const mat = mesh.material;
        if (mat instanceof THREE.MeshStandardMaterial) {
          parts.push({ mat, base: mat.emissiveIntensity });
        }
      };
      take(o.userData.lampTube as THREE.Mesh | undefined);
      o.traverse((c) => {
        if (c.name === 'lamp_lip') take(c as THREE.Mesh);
      });
      const glowMesh = o.userData.lampGlow as THREE.Mesh | undefined;
      const glowMat =
        glowMesh?.material instanceof THREE.MeshBasicMaterial ? glowMesh.material : null;
      if (parts.length === 0 && !glowMat) return;
      this.lamps.push({
        root: o,
        parts,
        glow: glowMat,
        glowBase: glowMat?.opacity ?? 0.55,
        phase: Math.random() * Math.PI * 2,
        speed: 4.2 + Math.random() * 6.5,
        sick: Math.random() < 0.2,
        blackout: 0,
      });
    });
  }

  update(dt: number): void {
    this.t += dt;
    for (const lamp of this.lamps) {
      if (lamp.root.userData.destroyed) {
        for (const p of lamp.parts) p.mat.emissiveIntensity = 0;
        if (lamp.glow) lamp.glow.opacity = 0;
        continue;
      }
      if (lamp.sick && lamp.blackout <= 0 && Math.random() < dt * 0.32) {
        lamp.blackout = 2 + ((Math.random() * 3) | 0);
      }
      if (lamp.blackout > 0) {
        lamp.blackout--;
        for (const p of lamp.parts) p.mat.emissiveIntensity = p.base * 0.05;
        if (lamp.glow) lamp.glow.opacity = lamp.glowBase * 0.06;
        continue;
      }
      const n = 0.5 + 0.5 * Math.sin(this.t * lamp.speed + lamp.phase);
      const mul = lamp.sick ? 0.55 + 0.45 * n : 0.82 + 0.18 * n;
      for (const p of lamp.parts) p.mat.emissiveIntensity = p.base * mul;
      if (lamp.glow) lamp.glow.opacity = lamp.glowBase * mul;
    }
  }
}
